import * as React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { withTranslation } from 'react-i18next';
import { UNAVAILABILITY_CONFIG, CALENDAR_NOTE_CONFIG } from './calendarConstants';
import useCalendarColors from './hooks/useCalendarColors';
import useCalendarState from './hooks/useCalendarState';
import { LOAD_BANDS, LEGEND_BANDS } from './hooks/monthUtils';

export function CalendarLegendContent({ t }) {
  // Statuses and tags both come from the organization's own settings, so the legend is built
  // from the resolver rather than from the built-in palette.
  const { statusEntries, activeTags } = useCalendarColors();
  const isMonthMode = useCalendarState((s) => s.calendarMode === 'month');

  return (
    <>
      {/* Utilization bands, month mode only */}
      {isMonthMode && (
        <>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>
            {t('calendar.month.summary.utilization')}:
          </Typography>
          {LEGEND_BANDS.map((key) => (
            <Box
              key={key}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '12px', color: 'text.secondary' }}
            >
              <Box sx={{ width: 14, height: 6, borderRadius: '3px', bgcolor: LOAD_BANDS[key].color }} />
              <span>{t(LOAD_BANDS[key].labelKey)}</span>
            </Box>
          ))}
          <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />
        </>
      )}

      {/* Status Legend */}
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>
        {t('Statuses')}:
      </Typography>
      {statusEntries.map(([key, config]) => (
        <Box
          key={key}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '12px', color: 'text.secondary' }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: config.color,
            }}
          />
          <span>{t(config.labelKey)}</span>
        </Box>
      ))}

      {/* Divider */}
      <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />

      {/* Unavailability Legend */}
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>
        {t('Absences')}:
      </Typography>
      {Object.entries(UNAVAILABILITY_CONFIG).map(([key, config]) => (
        <Box
          key={key}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '12px', color: 'text.secondary' }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: config.color,
            }}
          />
          <span>{t(config.labelKey)}</span>
        </Box>
      ))}

      {/* Colour tag legend (only when the organization has defined tags) */}
      {activeTags.length > 0 && (
        <>
          <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>
            {t('calendar.colorTags.legendTitle')}:
          </Typography>
          {activeTags.map((tag) => (
            <Box
              key={tag.id}
              data-testid={`calendar-legend-color-tag-${tag.id}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '12px', color: 'text.secondary' }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  // A ring tag is outlined in the legend too, so the two modes stay apart.
                  bgcolor: tag.display_mode === 'border' ? 'transparent' : tag.color,
                  border: tag.display_mode === 'border' ? `2px solid ${tag.color}` : 'none',
                }}
              />
              <span>{tag.name}</span>
            </Box>
          ))}
        </>
      )}

      {/* Divider */}
      <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />

      {/* Calendar Note Legend */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '12px', color: 'text.secondary' }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: CALENDAR_NOTE_CONFIG.color,
          }}
        />
        <span>{t('Note')}</span>
      </Box>

      {/* Divider */}
      <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />

      {/* Hint */}
      <Typography sx={{ fontSize: '12px', color: 'text.disabled', fontStyle: 'italic' }}>
        {isMonthMode ? t('calendar.month.tip') : t('Tip drag to create')}
      </Typography>
    </>
  );
}

function CalendarLegend({ t, onHide }) {
  return (
    <Box
      data-testid="calendar-legend"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pl: 3,
        // The help chat's floating button (HelpWidget: fixed, 56 px, 24 px from the right)
        // sits over this strip's right end; keep the last entry and the hide button clear
        // of it, like the collapsed legend's button does with its right: 90.
        pr: '90px',
        py: 1.5,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        flexShrink: 0,
        flexWrap: 'wrap',
      }}
    >
      <CalendarLegendContent t={t} />
      {onHide && (
        <IconButton
          onClick={onHide}
          size="small"
          sx={{
            ml: 'auto',
            p: 0.25,
            color: 'text.disabled',
            '&:hover': { color: 'text.secondary' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}

export default withTranslation()(CalendarLegend);
